<template>
  <portal-layout>
    <div v-loading="loading" class="detail-page">
      <div class="page-head">
        <h2 class="page-title">学习计划详情</h2>
        <el-button type="text" class="back-btn" @click="goBack">
          <i class="el-icon-arrow-left"></i> 返回学习报告
        </el-button>
      </div>

      <div v-if="!loading && !plan.id" class="empty-tip">学习计划不存在</div>

      <template v-if="plan.id">
        <!-- 计划概要 -->
        <div class="summary-card">
          <!-- 课程封面（courseMainPic 有值才显示） -->
          <div v-if="plan.courseMainPic" class="summary-cover">
            <img :src="plan.courseMainPic" alt="" />
          </div>
          <div class="summary-main">
            <div class="summary-title">
              <span class="course-name">{{ plan.courseName }}</span>
              <el-tag size="mini" :type="plan.teachingFormat === 'live' ? 'danger' : ''" effect="plain">
                {{ formatTeachingFormat(plan.teachingFormat) }}
              </el-tag>
              <el-tag v-if="isPlanFinished" size="mini" type="info">{{ statusText(plan.status) }}</el-tag>
            </div>
            <div class="summary-meta">
              <span class="meta-item"><strong>小主：</strong>{{ plan.studentName || '-' }}</span>
              <span class="meta-item"><strong>课程分类：</strong>{{ plan.cateName || '-' }}</span>
              <span class="meta-item"><strong>适用年级：</strong>{{ plan.fitGrade || '-' }}</span>
              <span class="meta-item"><strong>总课次：</strong>{{ plan.courseNum || 0 }} 节</span>
              <span class="meta-item">
                <strong>学习进度：</strong>
                <span class="progress-text">{{ startedCount }} / {{ sections.length }}</span>
              </span>
            </div>
            <div class="summary-meta second">
              <span v-if="plan.videoMainTitle" class="meta-item">
                <strong>课程视频：</strong>{{ plan.videoMainTitle }}
              </span>
              <span v-if="plan.commodityTitle" class="meta-item">
                <strong>关联商品：</strong>{{ plan.commodityTitle }}
              </span>
              <span v-if="!plan.videoMainTitle && !plan.commodityTitle" class="meta-item">暂无关联信息</span>
            </div>
          </div>
        </div>

        <!-- 课程安排 -->
        <div class="section-card">
          <div class="section-head">
            <div class="section-title">
              课程安排<template v-if="sections.length > 0">（共 {{ sections.length }} 节）</template>
            </div>
            <div class="section-legend">
              <span class="legend-item"><i class="dot dot-done"></i>已开始</span>
              <span class="legend-item"><i class="dot dot-wait"></i>未开始</span>
            </div>
          </div>

          <div v-if="sections.length === 0" class="empty-tip small">
            暂未排课
            <el-button type="text" @click="goContact">联系老师</el-button>
          </div>

          <template v-else>
            <!-- 录播课 -->
            <div v-for="item in sections" v-show="plan.teachingFormat === 'recorded'"
                 :key="'v' + item.section" class="section-item">
              <!-- 视频封面 -->
              <div class="item-cover" @click="watchVideo(item)">
                <img v-if="item.videoCoverUrl && !coverFailed[coverKey(item)]"
                     :src="item.videoCoverUrl" alt="" @error="onCoverError(item)" />
                <div v-else class="cover-placeholder">
                  <i :class="item.videoStatus === 1 ? 'el-icon-video-play' : 'el-icon-video-camera-solid'"></i>
                  <span>{{ item.videoStatus === 1 ? '可播放' : '待上传' }}</span>
                </div>
                <span v-if="item.videoStatus === 1" class="cover-mask"><i class="el-icon-caret-right"></i></span>
              </div>

              <div class="item-body">
                <div class="item-head">
                  <span class="item-index">第 {{ item.section }} 节课</span>
                  <el-tag size="mini" :type="item.videoStatus === 1 ? 'success' : 'info'">
                    {{ videoStatusText(item.videoStatus) }}
                  </el-tag>
                  <span v-if="item.videoId" class="item-id">视频ID：{{ item.videoId }}</span>
                </div>
                <div class="teacher-line">
                  <img v-if="item.teacherAvatar" class="teacher-avatar" :src="item.teacherAvatar" alt="" />
                  <span class="teacher-name">
                    {{ item.teacherName || '待定' }}<template v-if="plan.cateName">（{{ plan.cateName }}）</template>
                  </span>
                </div>
                <div class="item-line">
                  <i class="el-icon-video-play"></i>
                  <span>
                    {{ item.videoTitle || '暂无学习内容' }}<template v-if="plan.courseName">（{{ plan.courseName }}）</template>
                  </span>
                </div>
                <div class="item-line sub">
                  小主姓名：{{ plan.studentName }}
                  <template v-if="item.createTime"> · 创建时间：{{ item.createTime }}</template>
                </div>
              </div>

              <div class="item-action">
                <el-button type="primary" size="small"
                           :disabled="item.videoStatus !== 1" @click="watchVideo(item)">
                  {{ item.videoStatus === 1 ? '观看视频' : '未开始' }}
                </el-button>
              </div>
            </div>

            <!-- 直播课 -->
            <div v-for="item in sections" v-show="plan.teachingFormat === 'live'"
                 :key="'l' + item.section" class="section-item">
              <!-- 视频封面 / 回放封面 -->
              <div class="item-cover" @click="handleCoverClick(item)">
                <img v-if="item.videoCoverUrl && !coverFailed[coverKey(item)]"
                     :src="item.videoCoverUrl" alt="" @error="onCoverError(item)" />
                <div v-else class="cover-placeholder live">
                  <i :class="liveCoverIcon(item)"></i>
                  <span>{{ liveCoverText(item) }}</span>
                </div>
                <span v-if="isLiveOn(item)" class="cover-live-tag">直播中</span>
                <span v-else-if="canReplay(item)" class="cover-mask"><i class="el-icon-caret-right"></i></span>
              </div>

              <div class="item-body">
                <div class="item-head">
                  <span class="item-index">
                    {{ isUnscheduled(item) ? '未排课' : (item.spreadDay || `第 ${item.section} 节课`) }}
                    <template v-if="plan.courseName">（{{ plan.courseName }}）</template>
                  </span>
                  <el-tag v-if="item.onlineOrOffline" size="mini"
                          :type="item.onlineOrOffline === 'online' ? 'success' : 'warning'">
                    {{ item.onlineOrOffline === 'online' ? '线上' : '线下' }}
                  </el-tag>
                  <el-tag v-if="item.liveStatus !== undefined && item.liveStatus !== null"
                          size="mini" :type="liveTagType(item.liveStatus)">
                    {{ liveStatusText(item.liveStatus) }}
                  </el-tag>
                </div>
                <div class="teacher-line">
                  <img v-if="item.teacherAvatar" class="teacher-avatar" :src="item.teacherAvatar" alt="" />
                  <span class="teacher-name">
                    {{ item.teacherName || '待定' }}<template v-if="plan.cateName">（{{ plan.cateName }}）</template>
                  </span>
                </div>
                <div class="item-line">
                  <i class="el-icon-time"></i>
                  <span v-if="isUnscheduled(item)">请联系老师进行排课</span>
                  <span v-else-if="item.beginTime || item.endTime">
                    {{ item.beginTime }} - {{ item.endTime }}
                    <span v-if="isLiveOn(item)" class="live-badge">直播中</span>
                  </span>
                  <span v-else>时间待定</span>
                </div>
                <div class="item-line sub">
                  小主姓名：{{ plan.studentName }}
                  <template v-if="item.roomId"> · 教室ID：{{ item.roomId }}</template>
                </div>
              </div>

              <div class="item-action">
                <el-button v-if="isUnscheduled(item)" size="small" disabled>未排课</el-button>
                <el-button v-else-if="isLiveStatus(item, '0')" size="small" disabled>未开始</el-button>
                <el-button v-else-if="isLiveOn(item)" type="primary" size="small"
                           @click="enterLive(item)">进入直播</el-button>
                <el-button v-else-if="isLiveStatus(item, '2')" size="small" disabled>直播结束</el-button>
                <el-button v-else-if="isLiveStatus(item, '3')" type="primary" size="small"
                           @click="watchReplay(item)">直播回放</el-button>
                <el-button v-else size="small" disabled>未知状态</el-button>
              </div>
            </div>
          </template>
        </div>
      </template>
    </div>
  </portal-layout>
</template>
<script>
import PortalLayout from '@/components/PortalLayout.vue';
import api from '@/common/api';

export default {
  name: 'PortalReportDetail',
  components: { PortalLayout },
  data() {
    return {
      loading: false,
      planId: null,
      plan: {},
      sections: [],
      // 封面加载失败的 key 集合（CDN 图片可能过期/被清理，失败后回落到占位块）
      coverFailed: {}
    }
  },
  computed: {
    // 已开始（录播 videoStatus=1 / 直播已有回放视频）的节数，用于展示学习进度
    startedCount() {
      return this.sections.filter((item) => String(item.videoStatus) === '1').length;
    },
    // 计划整体状态 1正常 9结束
    isPlanFinished() {
      return String(this.plan.status) === '9';
    }
  },
  created() {
    this.planId = this.$route.query.id;
    if (!this.planId) {
      this.$message.error('缺少学习计划参数');
      return;
    }
    this.loadPlanDetail();
  },
  methods: {
    loadPlanDetail() {
      this.loading = true;
      api.lean_section({ id: this.planId }).then((r) => {
        if (r.data.code === "200" && r.data.data) {
          this.plan = r.data.data;
          this.sections = r.data.data.sections || [];
          this.coverFailed = {};
        }
      }).catch(() => {
        // 401 已由 common/api.js 统一跳转登录页
      }).finally(() => {
        this.loading = false;
      });
    },
    /* ------------------------- 文案映射 ------------------------- */
    // 教学形式：live直播课 recorded录播课（兜底原文显示）
    formatTeachingFormat(format) {
      const map = { live: '直播', recorded: '录播' };
      return map[format] !== undefined ? map[format] : (format || '-');
    },
    // 计划状态 1正常 9结束
    statusText(status) {
      const map = { 1: '进行中', 9: '已结束' };
      return map[String(status)] !== undefined ? map[String(status)] : '';
    },
    // 录播：videoStatus 0未开始 1已上传可观看（其它值兜底为未知）
    videoStatusText(status) {
      const map = { 0: '未开始', 1: '已开始' };
      return map[status] !== undefined ? map[status] : '未知状态';
    },
    // 直播：liveStatus 0未开始 1直播中 2结束 3回放 9未排课
    liveStatusText(status) {
      const map = { 0: '未开始', 1: '直播中', 2: '已结束', 3: '可回放', 9: '未排课' };
      return map[String(status)] !== undefined ? map[String(status)] : '未知状态';
    },
    liveTagType(status) {
      const map = { '1': 'danger', '3': 'success', '0': '', '2': 'info', '9': 'info' };
      return map[String(status)] !== undefined ? map[String(status)] : 'info';
    },
    /*
     * 注意：同一个响应内字段类型是混用的 —— teachingFormat 是字符串 'live'，
     * 而 sections[].liveStatus / videoStatus 是数字（后端 Short/String 混排）。
     * 因此所有状态判断统一用 String() 归一化后再比较，避免类型不一致导致分支永不生效。
     */
    isLiveStatus(item, val) {
      return String(item.liveStatus) === val;
    },
    isUnscheduled(item) {
      return String(item.liveStatus) === '9';
    },
    isLiveOn(item) {
      return String(item.liveStatus) === '1';
    },
    // 有回放视频（videoId）且不是未排课状态，视为可回放
    canReplay(item) {
      return !!item.videoId && !this.isUnscheduled(item);
    },
    liveCoverIcon(item) {
      if (this.isLiveOn(item)) return 'el-icon-video-camera-solid';
      if (this.canReplay(item)) return 'el-icon-video-play';
      if (this.isUnscheduled(item)) return 'el-icon-warning-outline';
      return 'el-icon-time';
    },
    liveCoverText(item) {
      if (this.isLiveOn(item)) return '直播中';
      if (this.canReplay(item)) return '回放';
      if (this.isUnscheduled(item)) return '未排课';
      return this.liveStatusText(item.liveStatus);
    },
    /* ------------------------- 封面处理 ------------------------- */
    coverKey(item) {
      return (this.plan.teachingFormat || '') + '-' + item.section;
    },
    onCoverError(item) {
      // Vue2 对对象新增属性不响应，需用 $set 保证占位块能渲染
      this.$set(this.coverFailed, this.coverKey(item), true);
    },
    // 直播课点封面：可回放则跳播放页，直播中则进直播间，其它不响应
    handleCoverClick(item) {
      if (this.isLiveOn(item)) {
        this.enterLive(item);
      } else if (this.canReplay(item)) {
        this.watchReplay(item);
      }
    },
    /* ------------------------- 跳转 ------------------------- */
    // 门户端直接跳转站内播放页，无需复制链接、无需免登桥接
    // 注意：playvideo.vue 内部会校验 query.status === 'success'（原「小程序→Web 免登」链路的遗留约定），
    // 站内直接跳转必须显式带上 status=success，否则会被判定为请求异常。
    watchVideo(item) {
      // 未上传视频时给出提示（videoStatus 1 表示已上传）
      if (item.videoStatus !== 1) {
        this.$message.warning('该视频暂未上传，敬请期待');
        return;
      }
      this.$router.push({
        path: '/playvideo',
        query: { id: this.planId, section: item.section, status: 'success' }
      }).catch(() => {});
    },
    // 直播回放同样复用录播播放页，但在新标签页打开
    // 用 router.resolve 生成站内 URL（自带与 $router.push 一致的 base 前缀与 query 编码），
    // 再用 window.open 开新页；不用 <a target="_blank"> 是为了避免 vue-router 拦截成同页跳转。
    watchReplay(item) {
      const { href } = this.$router.resolve({
        path: '/playvideo',
        query: { id: this.planId, section: item.section, status: 'success' }
      });
      window.open(href, '_blank');
    },
    // 进入直播教室：新标签页打开（直播教室为全屏独立页）
    // 与直播回放 watchReplay 一致：router.resolve 生成站内 URL（自带与 $router.push 一致的 base 前缀与 query 编码），
    // 再用 window.open 开新页；不用 <a target="_blank"> 是为了避免 vue-router 拦截成同页跳转。
    enterLive(item) {
      const { href } = this.$router.resolve({
        path: '/livevideo',
        query: { id: this.planId, section: item.section }
      });
      window.open(href, '_blank');
    },
    goContact() {
      this.$router.push({
        path: '/portal/shop/contact',
        query: {}
      }).catch(() => {});
    },
    goBack() {
      this.$router.push('/portal/report/list').catch(() => {});
    }
  }
};
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
.page-title {
  font-size: 22px;
  color: #303133;
  margin: 0;
}
.back-btn {
  font-size: 14px;
}

/* ---------- 计划概要 ---------- */
.summary-card {
  display: flex;
  align-items: flex-start;
  gap: 22px;
  background-color: #fff;
  border-radius: 8px;
  padding: 22px 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}
.summary-cover {
  flex-shrink: 0;
  width: 180px;
  height: 108px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f5f7fa;
}
.summary-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.summary-main {
  flex: 1;
  min-width: 0;
}
.summary-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}
.course-name {
  font-size: 18px;
  color: #303133;
  font-weight: 500;
}
.summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}
.summary-meta.second {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ebeef5;
}
.meta-item {
  font-size: 13px;
  color: #606266;
}
.meta-item strong {
  color: #909399;
  font-weight: normal;
}
.progress-text {
  color: #409EFF;
  font-weight: 500;
}

/* ---------- 课程安排 ---------- */
.section-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 22px 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.section-title {
  font-size: 17px;
  color: #303133;
  font-weight: 500;
  padding-left: 10px;
  border-left: 3px solid #409EFF;
  line-height: 20px;
}
.section-legend {
  display: flex;
  align-items: center;
  gap: 16px;
}
.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
}
.legend-item .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 5px;
}
.dot-done {
  background-color: #67C23A;
}
.dot-wait {
  background-color: #C0C4CC;
}

.section-item {
  display: flex;
  align-items: center;
  padding: 18px 0;
  border-bottom: 1px solid #ebeef5;
}
.section-item:last-child {
  border-bottom: none;
}

/* 封面 */
.item-cover {
  position: relative;
  flex-shrink: 0;
  width: 120px;
  height: 72px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f5f7fa;
  cursor: pointer;
}
.item-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s;
}
.item-cover:hover img {
  transform: scale(1.06);
}
.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #C0C4CC;
  background: linear-gradient(135deg, #f5f7fa 0%, #e9edf2 100%);
}
.cover-placeholder i {
  font-size: 20px;
}
.cover-placeholder span {
  font-size: 11px;
}
.cover-placeholder.live {
  color: #B3B9C2;
}
.cover-mask {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 30px;
  height: 30px;
  margin: -15px 0 0 -15px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 15px;
  pointer-events: none;
}
.cover-live-tag {
  position: absolute;
  left: 6px;
  top: 6px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 17px;
  color: #fff;
  background-color: #F56C6C;
  border-radius: 3px;
}

/* 信息区 */
.item-body {
  flex: 1;
  min-width: 0;
  padding: 0 20px;
}
.item-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.item-index {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}
.item-id {
  font-size: 12px;
  color: #C0C4CC;
}
.teacher-line {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.teacher-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}
.teacher-name {
  font-size: 13px;
  color: #606266;
}
.item-line {
  font-size: 13px;
  color: #606266;
  line-height: 22px;
}
.item-line i {
  color: #409EFF;
  margin-right: 6px;
}
.item-line.sub {
  font-size: 12px;
  color: #909399;
}
.live-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 18px;
  color: #fff;
  background-color: #F56C6C;
  border-radius: 3px;
}
.item-action {
  flex-shrink: 0;
  width: 110px;
  text-align: right;
}

/* ---------- 空态 ---------- */
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 60px 0;
  font-size: 14px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.empty-tip.small {
  padding: 30px 0;
  box-shadow: none;
}

/* ---------- 窄屏适配 ---------- */
@media (max-width: 900px) {
  .summary-card {
    flex-direction: column;
  }
  .summary-cover {
    width: 100%;
    height: 160px;
  }
  .section-item {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .item-body {
    padding: 0 0 0 15px;
  }
  .item-action {
    width: 100%;
    text-align: left;
    padding: 10px 0 0 135px;
  }
}
</style>
